import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import type React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAccount } from '../../../contexts/AccountContext';
import { useSnackbar } from '../../../contexts/SnackbarContext';
import { createTeam, getTeam, getTeamsByClient } from '../../../services/teamService';
import { getUsers, getUsersByTeam } from '../../../services/userService';
import { PERMISSIONS } from '../../../shared/constants/permissions';
import type { User } from '../../../types/auth';
import { StandardTable } from '../../common/UI/StandardTable';

interface TeamLimits {
  numTenders?: number;
  numDrafts?: number;
  numReviews?: number;
  numReports?: number;
  numAdHocDocs?: number;
  numSectionChats?: number;
}

interface Team {
  id: string;
  teamName: string;
  description?: string;
  teamLeadId?: string;
  limits?: TeamLimits;
}

interface TeamManagementPanelProps {
  clientId?: string;
  maxTeams?: number;
}

const TeamManagementPanel: React.FC<TeamManagementPanelProps> = ({
  clientId: propClientId,
  maxTeams = 1,
}) => {
  const { account, hasPermission } = useAccount();
  const { showSnackbar } = useSnackbar();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [teams, setTeams] = useState<Team[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);

  const targetClientId = propClientId || account?.clientId;

  // Permission Checks
  const canCreateTeam = hasPermission(PERMISSIONS.TEAM_CREATE);
  const canUpdateTeam = hasPermission(PERMISSIONS.TEAM_UPDATE);
  // If user cannot update teams (Admin), they are likely a Team Lead with restricted view
  const isRestrictedView = !canUpdateTeam;

  const handleEdit = useCallback(
    (team: Team) => {
      // Navigate to Team Details Page
      navigate(`/admin/team/${team.id}`);
    },
    [navigate]
  );

  // Auto-open team dialog if teamId param is present - Update to navigate?
  useEffect(() => {
    const teamIdParam = searchParams.get('teamId');
    if (teamIdParam) {
      navigate(`/admin/team/${teamIdParam}`);
    }
  }, [searchParams, navigate]);

  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newTeamName, setNewTeamName] = useState('');
  const [newTeamDescription, setNewTeamDescription] = useState('');

  const fetchUsers = useCallback(async () => {
    if (!targetClientId) {
      return;
    }
    try {
      if (isRestrictedView && account?.teamId) {
        // Team Leads cannot use getUsers API, but can query their own team members
        const teamUsers = await getUsersByTeam(account.teamId, targetClientId);
        setUsers(teamUsers);
      } else {
        // Admins use the API
        const response = await getUsers({ companyId: targetClientId, limit: 1000 });
        if (response.success && response.users) {
          setUsers(response.users);
        }
      }
    } catch (_err) {
      showSnackbar('Failed to fetch users', 'error');
    }
  }, [targetClientId, isRestrictedView, account?.teamId, showSnackbar]);

  const fetchTeams = useCallback(async () => {
    if (!targetClientId) {
      return;
    }
    setLoading(true);
    try {
      let teamsData: Team[] = [];
      if (isRestrictedView && account?.teamId) {
        // Team Lead can only see their own team
        const team = await getTeam(account.teamId);
        if (team) {
          teamsData = [team];
        }
      } else {
        teamsData = await getTeamsByClient(targetClientId);
      }

      setTeams(teamsData);
    } catch {
      showSnackbar('Failed to fetch teams', 'error');
    } finally {
      setLoading(false);
    }
  }, [targetClientId, showSnackbar, isRestrictedView, account?.teamId]);

  useEffect(() => {
    fetchTeams();
    fetchUsers();
  }, [fetchTeams, fetchUsers]);

  const handleCreate = async () => {
    if (!newTeamName.trim() || !targetClientId) {
      return;
    }

    if (newTeamName.length > 50) {
      showSnackbar('Team name must be 50 characters or less', 'error');
      return;
    }
    if (newTeamDescription.length > 255) {
      showSnackbar('Team description must be 255 characters or less', 'error');
      return;
    }

    if (teams.length >= maxTeams) {
      showSnackbar(`Team limit reached. You can only create ${maxTeams} teams.`, 'error');
      return;
    }

    try {
      await createTeam({
        teamName: newTeamName.trim(),
        description: newTeamDescription.trim(),
        clientId: targetClientId,
        limits: {
          numTenders: 0,
          numDrafts: 0,
          numReviews: 0,
          numReports: 0,
          numAdHocDocs: 0,
          numSectionChats: 0,
        },
      });
      showSnackbar('Team created successfully', 'success');
      setCreateDialogOpen(false);
      setNewTeamName('');
      setNewTeamDescription('');
      fetchTeams();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      showSnackbar(`Failed to create team: ${message}`, 'error');
    }
  };

  if (loading) {
    return <CircularProgress />;
  }

  const isUnlimitedTeams = maxTeams === Infinity || maxTeams === undefined;
  const atTeamLimit = !isUnlimitedTeams && teams.length >= maxTeams;

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" mb={2}>
        <Typography variant="h6">Team Management</Typography>
        <Box display="flex" alignItems="center" gap={2}>
          {!isUnlimitedTeams && (
            <Typography variant="body2" color={atTeamLimit ? 'error' : 'text.secondary'}>
              Teams: {teams.length} / {maxTeams}
            </Typography>
          )}
          {canCreateTeam && (
            <Button
              variant="contained"
              onClick={() => setCreateDialogOpen(true)}
              disabled={atTeamLimit}
            >
              Create Team
            </Button>
          )}
        </Box>
      </Box>
      <StandardTable
        columns={[
          { id: 'teamName', label: 'Team Name' },
          {
            id: 'teamLead',
            label: 'Team Lead',
            render: (team: Team) => {
              const lead = users.find((u) => u.uid === team.teamLeadId);
              return lead?.displayName || lead?.email || '-';
            },
          },
          {
            id: 'members',
            label: 'Members',
            render: (team: Team) => users.filter((u) => u.teamId === team.id).length,
          },
          {
            id: 'tenders',
            label: 'Allocated Tenders',
            render: (team: Team) =>
              team.limits?.numTenders === -1 || team.limits?.numTenders === Infinity
                ? 'Unlimited'
                : team.limits?.numTenders || 0,
          },
          {
            id: 'drafts',
            label: 'Allocated Drafts',
            render: (team: Team) =>
              team.limits?.numDrafts === -1 || team.limits?.numDrafts === Infinity
                ? 'Unlimited'
                : team.limits?.numDrafts || 0,
          },
          {
            id: 'chats',
            label: 'Allocated Chats',
            render: (team: Team) =>
              team.limits?.numSectionChats === -1 || team.limits?.numSectionChats === Infinity
                ? 'Unlimited'
                : team.limits?.numSectionChats || 0,
          },
        ]}
        data={teams}
        keyExtractor={(team) => team.id}
        onRowClick={handleEdit}
        emptyStateMessage="No teams found."
      />

      {/* Create Team Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Create New Team</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Team Name"
              value={newTeamName}
              onChange={(e) => setNewTeamName(e.target.value)}
              fullWidth
              autoFocus
              inputProps={{ maxLength: 50 }}
              helperText={`${newTeamName.length}/50`}
              error={newTeamName.length > 50}
            />
            <TextField
              label="Description (Optional)"
              value={newTeamDescription}
              onChange={(e) => setNewTeamDescription(e.target.value)}
              fullWidth
              multiline
              rows={3}
              inputProps={{ maxLength: 255 }}
              helperText={`${newTeamDescription.length}/255`}
              error={newTeamDescription.length > 255}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleCreate}
            variant="contained"
            disabled={
              !newTeamName.trim() || newTeamName.length > 50 || newTeamDescription.length > 255
            }
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TeamManagementPanel;
